/**
 * The project's board and plans on the console (B-17, A-19): the shared board's columns by status,
 * a task sheet with Take, Start, Done, Blocked and comments, and the plans kept at the home with
 * Approve, Abandon and the acceptance verdicts. Every write goes to the organisation's own routes
 * as the signed-in person, so nothing here needs a machine to be online. A plan with steps is drawn as the
 * workbench draws it (PlanGraph: waves of work and the critical path), and a finished plan being wrapped
 * up shows the workbench's Wrap-up card in place of its acceptance list.
 */

import { SubjectName } from './SubjectPicker.tsx';
import { ACCEPTANCE_VERDICT_TEXT, alongsideWords, myTaskMovePatch, planCriterionVerdict, planQueuedWords, taskStatusText, type AcceptanceVerdict, type BoardTask, type MyTaskMove, type PlanInput, type PlanWrapUpRecord, type ProjectPlanRecord } from '@aioe/shared';
import { LayoutColumns, NodesRight } from '@gravity-ui/icons';
import { Modal } from '@heroui/react';
import { Kanban } from '@heroui-pro/react';
import { useMemo, useState } from 'react';

import { routes } from '../api/client.ts';
import { useAuth } from '../auth/context.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { longestChain, PlanGraph, type PlanGraphStep, stepStatus } from './plans/PlanGraph.tsx';
import { WrapUpCard } from './plans/WrapUpCard.tsx';
import { wrapUpPlan } from './plans/wrapUpPlan.ts';
import { useRootFilter } from './RootFilter.tsx';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, SectionHeading, StatusMark } from './ui.tsx';
import { RowMenu } from './ui/RowMenu.tsx';

const COLUMNS = ['backlog', 'todo', 'in-progress', 'review', 'blocked', 'done'] as const;
const markOf = (status: string): 'idle' | 'working' | 'medium' | 'error' | 'low' => (status === 'in-progress' ? 'working' : status === 'review' ? 'medium' : status === 'blocked' ? 'error' : status === 'done' ? 'low' : 'idle');
const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e));
/** Each column's dot in the workbench's board colours (boardLook.ts), as the console's tones. */
const COLUMN_TONE: Record<(typeof COLUMNS)[number], string> = { backlog: 'slate', todo: 'blue', 'in-progress': 'amber', review: 'violet', blocked: 'rose', done: 'green' };
/** What an empty column means, in the workbench's words. */
const COLUMN_EMPTY: Record<(typeof COLUMNS)[number], string> = { backlog: 'Nothing waiting', todo: 'Nothing queued', 'in-progress': 'Nobody is working', review: 'Nothing to review', blocked: 'Nothing is blocked', done: 'Nothing finished yet' };

export function ProjectBoard({ projectId, part, canWrite }: { projectId: string; part: 'tasks' | 'plans'; canWrite: boolean }) {
  return part === 'plans' ? <ProjectPlans projectId={projectId} canWrite={canWrite} /> : <BoardColumns projectId={projectId} canWrite={canWrite} />;
}

function BoardColumns({ projectId, canWrite }: { projectId: string; canWrite: boolean }) {
  const { account } = useAuth();
  const boards = useLoad(() => routes.projectBoards(projectId), [projectId]);
  const [key, setKey] = useState<string>();
  const [mine, setMine] = useState(false);
  const filter = useRootFilter(projectId);
  const rows = (boards.data ?? []).filter((b) => filter.passes(b.rootId) || (b.rootId === '' && filter.root === '__all'));
  const chosen = rows.find((b) => `${b.rootId}\u0000${b.board}` === key) ?? rows[0];
  const read = useLoad(() => (chosen ? routes.boardRead(projectId, chosen.rootId, chosen.board) : Promise.resolve(undefined)), [projectId, chosen?.rootId, chosen?.board], 20_000);
  const [open, setOpen] = useState<string>();
  const tasks = (read.data?.tasks ?? []).filter((t) => !mine || t.assignee === account?.id);
  const sheet = tasks.find((t) => t.id === open) ?? read.data?.tasks.find((t) => t.id === open);
  const names: Record<string, string> = Object.fromEntries(rows.map((b) => [`${b.rootId}\u0000${b.board}`, `${b.rootId === '' ? 'Project board' : b.board === 'solo' ? 'Solo work' : b.board}${b.rootId ? ` · ${b.rootId}` : ''}`]));
  if (!rows.length && !boards.error) return <Empty tone="green" icon={<LayoutColumns />} title="No board yet" body="A workbench that keeps this project's board in the organisation creates one with its first task." />;
  return (
    <section className="list-group-section" aria-label="Board">
      <SectionHeading
        title="Board"
        count={tasks.length}
        description="Move your own work from here; no machine needs to be online."
        actions={
          <>
            {filter.control}
            {rows.length > 1 && <ChoiceSelect label="Board" className="w-fit min-w-44" value={`${chosen?.rootId}\u0000${chosen?.board}`} options={names} onChange={setKey} />}
            <Button size="sm" variant={mine ? 'secondary' : 'ghost'} aria-pressed={mine} onPress={() => setMine(!mine)}>
              Mine
            </Button>
          </>
        }
      />
      <ErrorNote error={boards.error ?? read.error} />
      {/* The workbench's board: a column per status with its dot, title and count, cards that open the task. */}
      <Kanban size="sm" className="project-board">
        {COLUMNS.map((col) => {
          const inCol = tasks.filter((t) => (t.status as string) === col);
          return (
            <Kanban.Column key={col}>
              <Kanban.ColumnHeader>
                <Kanban.ColumnIndicator style={{ background: `var(--tone-${COLUMN_TONE[col]})` }} />
                <Kanban.ColumnTitle>{taskStatusText(col)}</Kanban.ColumnTitle>
                <Kanban.ColumnCount>{inCol.length}</Kanban.ColumnCount>
              </Kanban.ColumnHeader>
              <Kanban.ColumnBody>
                <Kanban.CardList
                  aria-label={`${taskStatusText(col)} tasks`}
                  items={inCol}
                  dependencies={[account?.id]}
                  renderEmptyState={() => <p className="m-0 px-2 py-6 text-center text-xs text-muted">{COLUMN_EMPTY[col]}</p>}
                >
                  {(t: BoardTask) => (
                    <Kanban.Card id={t.id} textValue={`${t.id} ${t.title}`} onAction={() => setOpen(t.id)}>
                      <div className="flex w-full min-w-0 flex-col gap-1">
                        <span className="text-xs text-muted">{t.id}</span>
                        <p className="m-0 text-sm font-medium leading-snug text-foreground">{t.title}</p>
                        <span className="truncate text-xs text-muted">{t.assignee ? (t.assignee === account?.id ? 'You' : <SubjectName id={t.assignee} fallback="Someone else" />) : 'Nobody yet'}</span>
                      </div>
                    </Kanban.Card>
                  )}
                </Kanban.CardList>
              </Kanban.ColumnBody>
            </Kanban.Column>
          );
        })}
      </Kanban>
      {sheet && chosen && <TaskSheet projectId={projectId} rootId={chosen.rootId} board={chosen.board} task={sheet} me={account?.id} canWrite={canWrite} onClose={() => setOpen(undefined)} onChange={read.reload} />}
    </section>
  );
}

/** One task: its words, the four moves and the conversation. The moves are the person's own. */
export function TaskSheet({ projectId, rootId, board, task, me, canWrite, onClose, onChange }: { projectId: string; rootId: string; board: string; task: BoardTask; me?: string; canWrite: boolean; onClose: () => void; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [comment, setComment] = useState('');
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await f();
      onChange();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const move = (m: MyTaskMove) => run(() => routes.boardPatchTask(projectId, rootId, board, task.id, task.version, myTaskMovePatch(m, me ?? '')));
  const mineNow = Boolean(me) && task.assignee === me;
  return (
    <Modal isOpen onOpenChange={(o) => !o && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size="md">
          <Modal.Dialog aria-label={`Task ${task.id}`}>
            <Modal.CloseTrigger />
            <Modal.Header className="flex-col items-start gap-1">
              <Modal.Heading>{`${task.id} ${task.title}`}</Modal.Heading>
              <StatusMark state={markOf(task.status)} label={taskStatusText(task.status)} />
            </Modal.Header>
            <Modal.Body className="grid gap-3 text-sm">
              <p className="m-0 text-muted">{task.assignee ? (mineNow ? 'Assigned to you.' : <>Assigned to <SubjectName id={task.assignee} fallback="someone else" />.</>) : 'Nobody has this yet.'}</p>
              {task.description && <p className="m-0 whitespace-pre-wrap">{task.description}</p>}
              {canWrite && (
                <div className="flex flex-wrap gap-2">
                  {!mineNow && (
                    <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => void move('take')}>
                      Take
                    </Button>
                  )}
                  {task.status !== 'in-progress' && (
                    <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => void move('start')}>
                      Start
                    </Button>
                  )}
                  {task.status !== 'blocked' && (
                    <Button size="sm" variant="ghost" isDisabled={busy} onPress={() => void move('blocked')}>
                      Blocked
                    </Button>
                  )}
                  {task.status !== 'done' && (
                    <Button size="sm" isDisabled={busy} onPress={() => void move('done')}>
                      Done
                    </Button>
                  )}
                </div>
              )}
              <ul className="m-0 grid list-none gap-2 p-0">
                {task.comments.map((c) => (
                  <li key={c.id} className="rounded-lg bg-surface-secondary/60 p-2">
                    <div className="text-xs text-muted">{`${c.author} · ${timeAgo(c.createdAt)}`}</div>
                    <div className="whitespace-pre-wrap">{c.body}</div>
                  </li>
                ))}
              </ul>
              {canWrite && (
                <form
                  className="grid gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (comment.trim()) void run(() => routes.boardComment(projectId, rootId, board, task.id, comment.trim()).then(() => setComment('')));
                  }}
                >
                  <textarea className={`${inputClass} min-h-16`} aria-label="Comment" placeholder="Add a comment" value={comment} onChange={(e) => setComment(e.target.value)} />
                  <div>
                    <Button size="sm" type="submit" variant="secondary" isDisabled={busy || !comment.trim()}>
                      Comment
                    </Button>
                  </div>
                </form>
              )}
              <ErrorNote error={error} />
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

const finished = (s: string) => s === 'done' || s === 'verified';

function ProjectPlans({ projectId, canWrite }: { projectId: string; canWrite: boolean }) {
  const plans = useLoad(() => routes.projectPlans(projectId), [projectId], 30_000);
  const wrapUps = useLoad(() => routes.wrapUps(projectId).catch(() => undefined), [projectId], 30_000);
  const filter = useRootFilter(projectId);
  const selected = filter.root;
  const list = useMemo(() => [...(plans.data?.plans ?? [])].filter((p) => p.status !== 'abandoned' && (selected === '__all' || p.rootId === selected)).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)), [plans.data, selected]);
  if (plans.error) return <ErrorNote error={plans.error} />;
  if (plans.data && !list.length) return <Empty tone="amber" icon={<NodesRight />} title="No plans yet" body="Plans come from Team Work in AI Workbench: a team drafts one, a person approves it here or there, and it is kept for the whole project." />;
  return (
    <>
      <SectionHeading title="Plans" count={list.length || undefined} description="The plans kept for the whole project, newest first: approve a draft, stop a plan or record its acceptance checks." actions={filter.control ?? undefined} />
      {list.map((p) => (
        <PlanPanel key={`${p.rootId}/${p.id}`} projectId={projectId} p={p} canWrite={canWrite} onChange={() => { plans.reload(); wrapUps.reload(); }} wrapUp={wrapUps.data?.wrapUps.find((w) => w.planId === p.id && w.rootId === p.rootId)} all={plans.data?.plans ?? []} live={(plans.data?.plans ?? []).filter((x) => x.id !== p.id && (x.status === 'approved' || x.status === 'dispatched'))} />
      ))}
    </>
  );
}

function PlanPanel({ projectId, p, canWrite, onChange, wrapUp, all, live }: { projectId: string; p: ProjectPlanRecord; canWrite: boolean; onChange: () => void; /** The plan's wrap-up, while its work is finished and it is not closed. */ wrapUp?: PlanWrapUpRecord; /** Every plan of the project, to find who else is on this plan's rows. */ all: ProjectPlanRecord[]; /** The plans that are under way, which this one may wait for (D247). */ live: ProjectPlanRecord[] }) {
  const plan = p.data as PlanInput & { outcome?: unknown; retrospective?: unknown };
  const steps = useMemo(() => planSteps(plan), [plan]);
  const critical = useMemo(() => planCritical(plan, steps), [plan, steps]);
  const [asking, setAsking] = useState<'approve' | 'abandon' | 'alongside'>();
  const [reason, setReason] = useState('');
  /** Approve now (`now`), or queue after a plan that is under way. */
  const [when, setWhen] = useState('now');
  const [error, setError] = useState<string>();
  const attempt = async (f: () => Promise<unknown>) => {
    setError(undefined);
    try {
      await f();
      onChange();
    } catch (e) {
      setError(messageOf(e));
    }
  };
  const queued = plan.status === 'draft' ? (plan.queuedAfter ?? undefined) : undefined;
  const criteria = plan.acceptance ?? [];
  // Rows of this plan that another team's plan under way holds: agreed ones read as a sentence, the rest offer "Work alongside" (D247).
  const rowTeam = (x: ProjectPlanRecord) => String((x.data as PlanInput).teamName ?? (x.data as PlanInput).ref ?? x.id);
  const agreed = (plan.rowAlongside ?? []).map((a) => alongsideWords(a.row, { teamName: String(plan.teamName ?? plan.ref ?? p.id), by: a.by ?? 'a member', with: { planId: a.planId, teamName: ((o) => (o ? rowTeam(o) : (a.planRef ?? 'Another team')))(all.find((x) => x.id === a.planId)) } }));
  const heldRow = finished(p.status) || p.status === 'abandoned' ? undefined : (plan.roadmap ?? []).flatMap((r) => all.filter((x) => x.id !== p.id && (x.status === 'approved' || x.status === 'dispatched') && (x.data as PlanInput).teamId !== plan.teamId && ((x.data as PlanInput).roadmap ?? []).some((y) => y.rootId === r.rootId && y.id === r.id) && !(plan.rowAlongside ?? []).some((a) => a.planId === x.id && a.row === r.id)).map((x) => ({ row: r, holder: x })))[0];
  const verdicts = criteria.map((c) => planCriterionVerdict(plan, c.id));
  const text = (v: unknown) => (typeof v === 'string' ? v : v && typeof v === 'object' && 'summary' in v && typeof (v as { summary: unknown }).summary === 'string' ? (v as { summary: string }).summary : undefined);
  const wrappingUp = Boolean(wrapUp && !plan.unify && p.status !== 'abandoned');
  const passed = verdicts.filter((v) => v === 'passed').length;
  return (
    <>
    <ListGroup
      title={plan.ref ?? plan.id}
      description={`${p.rootId ? `In ${p.rootId} · ` : ''}updated ${timeAgo(p.updatedAt)}`}
      actions={
        canWrite && !finished(p.status) ? (
          <>
            {p.status === 'draft' && (
              <Button size="sm" variant="primary" onPress={() => setAsking('approve')}>
                Approve
              </Button>
            )}
            <RowMenu label={`More for ${plan.ref ?? 'this plan'}`} items={[{ id: 'abandon', label: 'Stop this plan…', danger: true }]} onAction={() => setAsking('abandon')} />
          </>
        ) : undefined
      }
    >
      <ListRow
        title={plan.objective}
        description={queued ? `${planQueuedWords({ status: p.status, queuedAfter: queued })}; it is approved when that plan finishes.` : criteria.length && !wrappingUp ? `Acceptance: ${passed} of ${criteria.length} checks passed` : undefined}
        status={<StatusMark state={p.status === 'draft' ? 'medium' : finished(p.status) ? 'low' : 'working'} label={p.status === 'draft' ? (queued ? `Queued after ${queued.planRef ?? 'another plan'}` : 'Draft, waiting for approval') : p.status === 'verified' ? 'Done and verified' : p.status === 'dispatched' ? 'Under way' : p.status[0]!.toUpperCase() + p.status.slice(1)} />}
      />
      {agreed.map((w) => (
        <ListRow key={w} title={w} />
      ))}
      {heldRow && canWrite ? (
        <ListRow
          title={`${heldRow.row.id} is held by ${rowTeam(heldRow.holder)}, ${(heldRow.holder.data as PlanInput).ref ?? heldRow.holder.id}`}
          description="Both teams can be on the row: it is recorded on both plans, with your name."
          compactControl
          control={
            <Button size="sm" variant="secondary" onPress={() => setAsking('alongside')}>
              Work alongside
            </Button>
          }
        />
      ) : null}
      {steps.length > 0 && (
        <ListBlock>
          <PlanGraph planId={`${p.rootId}/${p.id}`} steps={steps} critical={critical} criteria={criteria.map((c, i) => ({ id: c.id, title: c.title ?? c.text ?? c.then ?? c.id, passed: verdicts[i] === 'passed' }))} />
        </ListBlock>
      )}
      {wrapUp && wrappingUp ? (
        <ListBlock>
          <WrapUpCard
            plan={wrapUpPlan(p, plan, wrapUp)}
            asks={wrapUp.asks}
            canWrite={canWrite}
            onAsk={async (ask) => {
              await routes.planWrapUpAsk(projectId, p.rootId, p.id, ask);
              onChange();
            }}
            onVerdict={async (criterionId, verdict) => {
              await routes.planAcceptance(projectId, p.rootId, p.id, criterionId, verdict);
              onChange();
            }}
          />
        </ListBlock>
      ) : null}
      {criteria.length > 0 && !wrappingUp
        ? criteria.map((c, i) => (
            <ListRow
              key={c.id}
              title={c.title ?? c.text ?? c.then ?? ''}
              description={`Acceptance check ${c.id}`}
              status={<StatusMark state={verdicts[i] === 'passed' ? 'low' : verdicts[i] === 'failed' ? 'error' : 'idle'} label={ACCEPTANCE_VERDICT_TEXT[verdicts[i]!]} />}
              compactControl
              control={
                canWrite ? (
                  <RowMenu
                    label={`Record ${c.id}`}
                    items={(['passed', 'failed'] as AcceptanceVerdict[]).filter((v) => v !== verdicts[i]).map((v) => ({ id: v, label: v === 'passed' ? 'Mark as passed' : 'Mark as failed' }))}
                    onAction={(v) => void attempt(() => routes.planAcceptance(projectId, p.rootId, p.id, c.id, v as AcceptanceVerdict))}
                  />
                ) : undefined
              }
            />
          ))
        : null}
      {plan.unify && <ListRow title="Outcome" description={<>{plan.unify.summary}{plan.unify.nextAction ? <span className="mt-1 block">{`Next: ${plan.unify.nextAction}`}</span> : null}</>} />}
      {text(plan.retrospective) && <ListRow title="Retrospective" description={<span className="whitespace-pre-wrap">{text(plan.retrospective)}</span>} />}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
      <ConfirmDialog
        isOpen={asking === 'approve'}
        title={`Approve ${plan.ref ?? 'this plan'}`}
        confirmLabel="Approve"
        onCancel={() => setAsking(undefined)}
        onConfirm={async () => {
          setAsking(undefined);
          const after = when === 'now' ? undefined : live.find((x) => `${x.rootId}/${x.id}` === when);
          await attempt(() => routes.planApprove(projectId, p.rootId, p.id, undefined, after ? { planId: after.id, rootId: after.rootId } : undefined));
        }}
      >
        Approving lets the team start the work. It is the same approval whichever screen you give it on, and the plan's author is told.
        {live.length > 0 && (
          <ChoiceSelect label="When" className="mt-3 w-full" value={when} options={{ now: 'Start now', ...Object.fromEntries(live.map((x) => [`${x.rootId}/${x.id}`, `Queue after ${(x.data as PlanInput).ref ?? x.id}`])) }} onChange={setWhen} />
        )}
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={asking === 'alongside' && Boolean(heldRow)}
        title={`Work alongside ${heldRow ? rowTeam(heldRow.holder) : 'the holder'}`}
        confirmLabel="Work alongside"
        onCancel={() => setAsking(undefined)}
        onConfirm={async () => {
          setAsking(undefined);
          if (heldRow) await attempt(() => routes.workAlongside(projectId, { rootId: heldRow.row.rootId, id: heldRow.row.id }, { planId: p.id, rootId: p.rootId }));
        }}
      >
        {heldRow ? `Both teams will be on ${heldRow.row.id}, ${rowTeam(heldRow.holder)} and ${rowTeam(p)}. It is recorded on both plans, with your name, and ${rowTeam(heldRow.holder)} keeps its hold.` : ''}
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={asking === 'abandon'}
        title={`Stop ${plan.ref ?? 'this plan'}`}
        confirmLabel="Stop the plan"
        danger
        onCancel={() => setAsking(undefined)}
        onConfirm={async () => {
          if (!reason.trim()) return;
          setAsking(undefined);
          await attempt(() => routes.planAbandon(projectId, p.rootId, p.id, reason.trim()));
          setReason('');
        }}
      >
        <span>The team stops working to this plan and nothing further is started from it. Work already done stays. Say why, so the author and the team know.</span>
        <textarea className={`${inputClass} min-h-16`} aria-label="Why the plan is stopped" placeholder="Why is it stopped?" value={reason} onChange={(e) => setReason(e.target.value)} />
      </ConfirmDialog>
    </>
  );
}

/** A plan's steps as the graph reads them: the home's step words, and what a workbench sent beside them. */
function planSteps(plan: PlanInput): PlanGraphStep[] {
  return (plan.nodes ?? []).map((n) => {
    const loose = n as typeof n & Partial<Pick<PlanGraphStep, 'estimate' | 'done' | 'files' | 'applyStatus' | 'concerns' | 'qualify' | 'outcome'>>;
    const status = stepStatus(n.status);
    return {
      ref: n.ref,
      title: n.title,
      dependsOn: n.dependsOn ?? [],
      ...(status ? { status } : {}),
      ...(n.taskId ? { taskId: n.taskId } : {}),
      ...(n.assignee ? { assignee: n.assignee } : {}),
      ...(n.role ? { role: n.role } : {}),
      ...(n.repository ? { repository: { rootId: n.repository.rootId, ...(n.repository.name ? { name: n.repository.name } : {}) } } : {}),
      ...(loose.estimate ? { estimate: loose.estimate } : {}),
      ...(Array.isArray(loose.done) ? { done: loose.done } : {}),
      ...(Array.isArray(loose.files) ? { files: loose.files } : {}),
      ...(typeof loose.outcome === 'string' ? { outcome: loose.outcome } : {}),
      ...(typeof loose.applyStatus === 'string' ? { applyStatus: loose.applyStatus } : {}),
      ...(typeof loose.concerns === 'string' ? { concerns: loose.concerns } : {}),
      ...(typeof loose.qualify === 'string' ? { qualify: loose.qualify } : {}),
    };
  });
}

/** The plan's own critical path when it sent one, else the longest chain of its steps. */
function planCritical(plan: PlanInput, steps: readonly PlanGraphStep[]): string[] {
  const own = (plan as { criticalPath?: unknown; critical?: unknown }).criticalPath ?? (plan as { critical?: unknown }).critical;
  return Array.isArray(own) && own.every((x) => typeof x === 'string') ? (own as string[]) : longestChain(steps);
}

